import { Injectable } from '@angular/core';
import { NetworkGroup } from '@netfoundry-ui/shared/model';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { LoggerService } from './logger.service';
import { MopResourceService } from './mopresource.service';

// how many ids ride in one find request: they travel in the query string, and a uuid list of this size
// stays well inside the request-line limits gateways commonly impose
const NETWORK_GROUP_ID_CHUNK = 100;

@Injectable({ providedIn: 'root' })
export class NetworkGroupService extends MopResourceService {
    networkGroupList: NetworkGroup[];
    private networkGroupSource = new Subject<NetworkGroup[]>();
    private networkGroups = this.networkGroupSource.asObservable();

    constructor(protected logger: LoggerService, private apiservice: ApiService) {
        super(logger, apiservice);
    }

    /**
     * Gets all Organizations
     */
    get(): Observable<any> {
        const params = {};
        this.apiService
            .get('network-groups', params, true)
            .pipe(map(this._extractEmbedded))
            .subscribe((orgs) => {
                this.networkGroupSource.next(orgs);
            });

        return this.networkGroups;
    }

    /**
     * Gets specific network groups by id, for callers that only need the ones they reference (role
     * display needs the groups the shown roles point at). Avoids the full list, which is large for an
     * NF-internal super-user.
     * The find endpoint filters on a set of ids, so a page of roles costs one request per chunk rather
     * than one per group - a tenant whose roles span hundreds of groups would otherwise fire hundreds of
     * requests. Ids are chunked because they travel in the query string, which gateways cap; the result
     * is paged, so `size` is set to the chunk or the page comes back truncated.
     */
    async getByIds(ids: string[]): Promise<NetworkGroup[]> {
        const wantedIds = [...new Set((ids || []).filter((id) => !!id))];
        const networkGroups: NetworkGroup[] = [];
        for (let index = 0; index < wantedIds.length; index += NETWORK_GROUP_ID_CHUNK) {
            const chunk = wantedIds.slice(index, index + NETWORK_GROUP_ID_CHUNK);
            networkGroups.push(...(await this.findByIdChunk(chunk)));
        }
        return networkGroups;
    }

    /**
     * Gets a single network group with its limits embedded (e.g. to check feature allowances like FIPS)
     */
    getWithLimits(id: string): Observable<any> {
        const params = { embed: 'network-group-limits' };
        return this.apiService.get(`network-groups/${id}`, params, true);
    }

    /**
     * Gets all network groups with their limits embedded, so callers can read e.g. fipsAllowed
     */
    getAllWithLimits(): Observable<any> {
        const params = { embed: 'network-group-limits' };
        return this.apiService.get('network-groups', params, true).pipe(map(this._extractEmbedded));
    }

    /**
     * Reads one chunk of ids in a single find request, resolving to an empty list when the request fails
     * - these reads back name resolution for display, so one unreadable chunk must not fail the caller.
     */
    private findByIdChunk(ids: string[]): Promise<NetworkGroup[]> {
        const params = { id: ids.join(','), page: 0, size: ids.length };
        return lastValueFrom(this.apiService.get('network-groups', params, true))
            .then((result: any) => this._extractEmbedded(result))
            .catch((error) => {
                this.logger.error(`Network group request for ${ids.length} id(s) failed`, error);
                return [] as NetworkGroup[];
            });
    }

    /**
     * Preprocesses the data before returning to the front-end
     */
    protected _extractEmbedded(res) {
        const networkGroups: NetworkGroup[] = [];
        let rows = [];
        if (res['_embedded'] !== undefined && res['_embedded']['organizations'] !== undefined) {
            rows = res['_embedded']['organizations'];
        } else if (res?._embedded?.networkGroupList?.length) {
            rows = res?._embedded?.networkGroupList;
        }
        for (let i = 0; i < rows.length; i++) {
            const networkGroup = rows[i];
            networkGroups.push(new NetworkGroup(networkGroup));
        }
        return networkGroups;
    }

    /**
     * Returns the create URL for this resource type
     */
    protected _getCreateUrl() {
        return 'network-groups';
    }
}
